"use client";

import { OptimizedImage } from "@/components/ui/optimized-image";
import ProductLink from "@/components/navigation/ProductLink";
import { Package, Star } from "lucide-react";
import type { Product } from "@/types";
import { useLocale, useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { Suspense, type ReactNode } from "react";
import { translateCityNames, translateCityName } from "@/config/locations";
import { citySeparator, cityListCandidates } from "@/lib/utils/format-cities";
import { FittedText } from "@/components/product-card/FittedText";
import { PriceDisplay } from "@/components/PriceDisplay";
import { bundleDaysOf } from "@/lib/pricing/base-plus-extra";
import { PartnerNameLink } from "@/components/product-card/PartnerNameLink";
import { recordAnalyticsEvent } from "@/app/actions/analytics";
import { getSessionId } from "@/lib/analytics/session";
import type { Locale } from "@/i18n/routing";

interface RealProductCardProps {
  product: Product;
  isUnavailable?: boolean;
  /**
   * 상세 링크를 통째로 정할 때(날짜·지점 쿼리까지 포함). 없으면 기본 링크에 검색 URL 의
   * 날짜를 붙인다. 캠핑 허브 [장비]는 지점을 미리 고른 링크(?branch=)를 넘긴다.
   */
  href?: string;
  /**
   * 가격 줄을 통째로 바꿀 때 — 기간 요금 상품(기본 N일 + 추가일)의 "₩… / 1박 2일".
   * 없으면 1일가 + "~". 예약 마감 표시는 이것보다 앞선다.
   */
  priceOverride?: ReactNode;
}

export default function RealProductCard({
  product,
  isUnavailable,
  href,
  priceOverride,
}: RealProductCardProps) {
  const locale = useLocale();
  const t = useTranslations("search");
  const tCommon = useTranslations("common");

  // Get the first image or use a placeholder
  const imageUrl = product.images?.[0] || "/placeholder.svg";

  // Show branch locations if available, otherwise fall back to primary_city
  // Translate city names based on current locale
  const translatedBranchLocations =
    product.branch_locations && product.branch_locations.length > 0
      ? translateCityNames(
          product.branch_locations,
          locale as Locale,
        )
      : [];

  const translatedPrimaryCity = product.primary_city
    ? translateCityName(
        product.primary_city,
        locale as Locale,
      )
    : null;

  // 지역 줄은 한 줄에 들어가는 만큼 보이고, 넘칠 때만 "외 N곳"(10-13). 첫 화면은 첫 도시 +
  // "외 N곳" — 지점 122곳짜리 상품도 줄이 무너지지 않는다(후보 규칙: lib/utils/format-cities).
  const locationCandidates =
    translatedBranchLocations.length > 0
      ? cityListCandidates(translatedBranchLocations, {
          separator: citySeparator(locale),
          moreLabel: (count) => tCommon("moreCities", { count }),
        })
      : translatedPrimaryCity
        ? [translatedPrimaryCity]
        : [];

  // Format price safely
  const pricePerDay = product.price_per_day || 0;
  // 기본 N일(2일 이상) 묶음 요금 상품만 금액 뒤에 "/ 1박 2일" — 나머지는 단위 없이 "~"(10-15)
  // 서버가 원본 요금을 빼고 미리 계산해 둔 값이 있으면 그것(10-16), 없으면 예전처럼 원본에서
  const bundleDays =
    product.bundleDays !== undefined ? product.bundleDays : bundleDaysOf(product.specifications);

  // 택배 전용 여부 (서버에서 미리 계산된 값 사용 - hydration mismatch 방지)
  const isDeliveryOnly = product.is_delivery_only ?? false;

  const productHref = `/${locale}/products/${product.id}`;
  const recordSelectItem = () => {
    // Funnel: select_item (product card click → detail).
    void recordAnalyticsEvent({
      eventType: "select_item",
      sessionId: getSessionId(),
      metadata: { product_id: product.id, source: "card" },
    }).catch(() => {});
  };

  // 루트는 div — 파트너명이 독립 링크라 루트가 <Link> 면 중첩 <a> 가 된다.
  // 상품 링크(상품명)가 after:inset-0 으로 카드 전체를 덮는다 (stretched link).
  return (
    <div className="group relative cursor-pointer min-w-0">
      <div className="relative aspect-square rounded-xl overflow-hidden mb-2 border border-black/8">
        <OptimizedImage
          src={imageUrl}
          alt={product.name}
          fill
          sizes="(max-width: 640px) 100vw, 33vw"
          className="object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {/* 택배 전용 뱃지 */}
        {!isUnavailable && isDeliveryOnly && (
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-white/60 backdrop-blur-md rounded-full shadow-sm">
            <Package className="w-4 h-4 text-gray-800" />
            <span className="text-sm sm:text-sm font-medium text-gray-900">
              {t("shippingOnly.badge")}
            </span>
          </div>
        )}
      </div>

      {/* 파트너 → 상품명 → 지역 → 가격. 데스크톱(md+)에서는 네 줄을 한 단계씩
          키운다 — 한 줄만 키우면 서열이 흐트러진다(09-23). */}
      <div className="mx-1 flex flex-col gap-0.5 min-w-0">
        {/* Partner Name — 1순위 앵커. 파트너 스토어로 가는 독립 링크. */}
        <PartnerNameLink
          name={product.partner_name}
          slug={product.partner_slug}
          className="text-[13px] leading-[18px] font-semibold text-gray-900 md:text-[14px] md:leading-[19px]"
        />

        {/* Title — 2순위. after:inset-0 이 카드 전체를 덮는 상품 링크.
            날짜 필터(?checkin/checkout)는 useSearchParams 로 읽는데, 정적 렌더(ISR)
            페이지(파트너 스토어 등)에서 그 훅이 카드 밖까지 CSR bailout 을 번지게 하지
            않도록 링크만 Suspense 로 감싼다. 서버 HTML 에는 날짜 없는 링크가 들어간다. */}
        {href ? (
          <ProductTitleLink href={href} onClick={recordSelectItem}>
            {product.name}
          </ProductTitleLink>
        ) : (
          <Suspense
            fallback={
              <ProductTitleLink href={productHref} onClick={recordSelectItem}>
                {product.name}
              </ProductTitleLink>
            }
          >
            <ProductTitleLinkWithDates
              href={productHref}
              onClick={recordSelectItem}
            >
              {product.name}
            </ProductTitleLinkWithDates>
          </Suspense>
        )}

        {/* Price / 예약마감 */}
        <div style={{ fontFamily: '"Pretendard Variable", sans-serif' }}
        >
          {isUnavailable ? (
            <span className="text-[16px] font-semibold text-gray-600 md:text-[17px]">
              {t("availability.soldOut")}
            </span>
          ) : priceOverride ? (
            priceOverride
          ) : pricePerDay <= 0 ? (
            // 가격을 모르면 "0~" 대신 줄을 비운다(10-15, 가격 없는 데이터 방어)
            null
          ) : (
            <span className="text-[16px] leading-[21px] font-bold tracking-tight text-gray-900 md:text-[17px] md:leading-[22px]">
              <PriceDisplay
                price={pricePerDay}
                sourceCurrency={product.currency}
                serverPrice={product.convertedPricePerDay}
                serverCurrency={product.displayCurrency}
                size="sm"
                showCurrencyCode
                className="text-[16px]! md:text-[17px]!"
              />
              ~
              {bundleDays ? (
                <span className="ml-1 text-[12px] font-normal tracking-normal text-gray-500 md:text-[13px]">
                  / {t("hub.camping.period", { nights: bundleDays - 1, days: bundleDays })}
                </span>
              ) : null}
            </span>
          )}
        </div>

        {/* Branch Location — 보조 정보, 가격 아래(10-13). 가격 위·아래 모두 카드 gap(2px)만 */}
        {locationCandidates.length > 0 && (
          <FittedText
            candidates={locationCandidates}
            className="text-[12px] leading-[16px] text-gray-500"
          />
        )}

        {/* Review rating + count (visible 리뷰가 있을 때만) — 가격 아래 */}
        {(product.review_count ?? 0) > 0 && (
          <div className="flex items-center gap-1 text-xs">
            <Star className="w-3.5 h-3.5 fill-orange-400 text-orange-400" />
            <span className="font-medium text-gray-900">
              {(product.avg_rating ?? 0).toFixed(1)}
            </span>
            <span className="text-gray-500">({product.review_count})</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface ProductTitleLinkProps {
  href: string;
  onClick: () => void;
  children: ReactNode;
}

function ProductTitleLink({ href, onClick, children }: ProductTitleLinkProps) {
  return (
    <ProductLink
      href={href}
      onClick={onClick}
      className="text-[14px] leading-[20px] font-normal text-gray-900 line-clamp-2 wrap-break-word after:absolute after:inset-0 after:content-[''] md:text-[15px] md:leading-[21px]"
    >
      {children}
    </ProductLink>
  );
}

/** 날짜 필터가 있으면 상세 페이지로 전달 */
function ProductTitleLinkWithDates({
  href,
  ...rest
}: ProductTitleLinkProps) {
  const searchParams = useSearchParams();
  const checkin = searchParams.get("checkin");
  const checkout = searchParams.get("checkout");
  const dateQuery =
    checkin && checkout ? `?checkin=${checkin}&checkout=${checkout}` : "";
  return <ProductTitleLink href={`${href}${dateQuery}`} {...rest} />;
}
